import {
  useLottiePlayer,
  usePhoneNumberForm,
  useTranslation,
} from '@starsoft/common/hooks';
import { FormProvider, useForm, UseFormReturn } from 'react-hook-form';
import styles from '../styles.module.scss';

import {
  Button,
  ErrorCard,
  TransTextWithDefault,
} from '@starsoft/common/components';
import { UserPhoneNumberFormProps } from '../props';
import UserPhoneNumberFormSkeleton from '../Skeleton';
import { UserPhoneNumberFormLayoutProps } from './props';
import { CreateDepositRequestDto } from '@starsoft/common/dto';

/**
 * UserPhoneNumberFormLayout Component
 * This component is responsible for rendering the layout of the user phone number form.
 * It manages form state, handles submission, and displays appropriate UI elements based on the form's state.
 *
 * @param {UserPhoneNumberFormLayoutProps} props - The properties for the component.
 * @param {React.ReactNode} props.children - The child components to be rendered within the form layout.
 *
 * @returns {JSX.Element} The rendered user phone number form layout component.
 *
 * @example
 * // Usage example:
 * <UserPhoneNumberFormLayout>
 *   <SomeChildComponent />
 * </UserPhoneNumberFormLayout>
 */
export default function UserPhoneNumberFormLayout({
  children,
}: UserPhoneNumberFormLayoutProps) {
  // Hook for translation in the 'account' namespace
  const { t } = useTranslation('account');

  // Initialize form methods using react-hook-form
  const methods: UseFormReturn<
    UserPhoneNumberFormProps & CreateDepositRequestDto
  > = useForm<UserPhoneNumberFormProps & CreateDepositRequestDto>();
  const { handleSubmit, setValue, watch } = methods;

  // Custom hook to manage phone number form state and actions
  const {
    error,
    hasCreatedUserPhoneNumber,
    isSkeleton,
    isSubmitDisabled,
    onError,
    refetch,
    isPendingVerifyUserPhoneNumber,
    country,
    onSubmit,
    isPendingCreatePhoneNumber,
    phoneNumber,
  } = usePhoneNumberForm({ watch, setValue });

  // Hook to manage Lottie animation
  const { lottieAnimationRef } = useLottiePlayer({
    path: '/phoneNumber.json',
    dependecyArray: [isSkeleton],
  });

  // Render error card if there's an error
  if (error) {
    return (
      <div className={styles.container}>
        <div className={styles.container__content}>
          <ErrorCard error={error} refetch={refetch} />
        </div>
      </div>
    );
  }

  // Render skeleton if the form is in a loading state
  if (isSkeleton) {
    return <UserPhoneNumberFormSkeleton />;
  }

  // Render the form layout
  return (
    <FormProvider {...methods}>
      <form
        className={styles.container}
        onSubmit={handleSubmit(onSubmit, onError)}
      >
        <div className={styles.container__content}>
          <div className={styles.container__content__column}>
            <div
              className={styles.container__content__lottie}
              ref={lottieAnimationRef}
            />
            <p className={styles.container__content__description}>
              <TransTextWithDefault
                text={t(
                  hasCreatedUserPhoneNumber
                    ? 'verify_phone_number_description'
                    : 'create_phone_number_description',
                  {
                    phoneNumber: `${country?.dialCode} ${phoneNumber}`,
                  },
                )}
              />
            </p>
          </div>

          {children}
        </div>

        <div className={styles.container__button}>
          <Button
            type="submit"
            isloading={
              isPendingCreatePhoneNumber || isPendingVerifyUserPhoneNumber
            }
            disabled={isSubmitDisabled}
          >
            {t(hasCreatedUserPhoneNumber ? 'verify' : 'continue')}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
